import { useQueries } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { format, parseISO } from 'date-fns';
import { Loader2, MoreHorizontal } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { GroupBase, StylesConfig } from 'react-select';

import type { MakeupBooking } from '~entities/makeup/api/book-makeup';
import {
  getMakeupBalancesAPI,
  type MakeupBalances,
} from '~entities/makeup/api/get-makeup-balances';
import { useGetMakeupSettings } from '~entities/makeup/api/get-makeup-settings';
import { useGetMakeupsByStudent } from '~entities/makeup/api/get-makeups-by-student';
import { useGetAllSessions } from '~entities/session/api/get-sessions';
import { useGetStudentById } from '~entities/student';
import type { Enrollment } from '~entities/student/modal/student';

import { ManageMakeupSheet } from '~features/classes/ui/manage-makeup-sheet';
import {
  ALL_SESSIONS_ID,
  ALL_SESSIONS_OPTION,
} from '~features/new-dashboard/constants';
import type { SessionOption } from '~features/shared/session-selector';

import { KEYS } from '~shared/api/query-keys';
import {
  EnrollioTable,
  EnrollioTableColumnToggle,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { defaultStyles } from '~shared/components/select/helper';
import Select from '~shared/components/select/select';
import { cn } from '~shared/shadcn/lib/utils';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~shared/shadcn/ui/dropdown-menu';

interface StudentMakeupsPageProps {
  studentId: string;
}

/** Portal the menu so the bookings table's sticky header can't overlap it. */
const sessionFilterStyles: StylesConfig<
  unknown,
  boolean,
  GroupBase<unknown>
> = {
  ...defaultStyles,
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

interface EnrolledSession {
  id: string;
  name: string;
}

interface SessionBalanceRow {
  sessionId: string;
  sessionName: string;
  balances?: MakeupBalances;
  isLoading: boolean;
  isError: boolean;
}

/** Calendar date fields (YYYY-MM-DD / UTC midnight) — use the date part only. */
function formatMakeupDate(value: string | undefined): string {
  if (!value) return '—';
  try {
    return format(parseISO(String(value).slice(0, 10)), 'MMM d, yyyy');
  } catch {
    return String(value).slice(0, 10);
  }
}

/** Instant timestamps — format in the user's local timezone. */
function formatBookedAt(value: string | undefined): string {
  if (!value) return '—';
  try {
    return format(parseISO(value), 'MMM d, yyyy');
  } catch {
    return String(value);
  }
}

function getEnrollmentTitle(
  enrollment:
    | MakeupBooking['sourceEnrollmentId']
    | MakeupBooking['targetEnrollmentId'],
): string {
  if (!enrollment) return '—';
  if (typeof enrollment === 'string') return 'Class';
  return enrollment.title ?? 'Class';
}

function getBookingSessionId(booking: MakeupBooking): string {
  const raw = booking.sessionId as string | { _id?: string } | undefined;
  if (!raw) return '';
  if (typeof raw === 'string') return raw;
  return raw._id ?? '';
}

function getEnrollmentSession(
  enrollment: Enrollment,
  sessionNameById: Map<string, string>,
): EnrolledSession | null {
  const classDetail = enrollment.enrollmentId;
  if (!classDetail || typeof classDetail === 'string') return null;

  const session = classDetail.session as
    | string
    | { _id?: string; name?: string }
    | undefined;
  if (!session) return null;

  if (typeof session === 'string') {
    return {
      id: session,
      name: sessionNameById.get(session) ?? 'Session',
    };
  }

  const id = session._id;
  if (!id) return null;
  return {
    id,
    name: session.name ?? sessionNameById.get(id) ?? 'Session',
  };
}

function formatCreditCell(remaining: number, limit: number): string {
  return `${remaining}/${limit} Remaining`;
}

function MakeupStatusBadge({ status }: { status: MakeupBooking['status'] }) {
  if (status === 'completed') {
    return (
      <Badge className='shrink-0 whitespace-nowrap border-transparent bg-emerald-100 text-emerald-800 hover:bg-emerald-100'>
        Made up
      </Badge>
    );
  }

  if (status === 'no_show') {
    return (
      <Badge className='shrink-0 whitespace-nowrap border-transparent bg-slate-100 text-slate-700 hover:bg-slate-100'>
        No show
      </Badge>
    );
  }

  if (status === 'cancelled') {
    return (
      <Badge
        variant='outline'
        className='border-destructive/30 bg-destructive/10 capitalize text-destructive hover:bg-destructive/10'
      >
        Cancelled
      </Badge>
    );
  }

  return (
    <Badge variant='secondary' className='capitalize'>
      {String(status).replace('_', ' ')}
    </Badge>
  );
}

export function StudentMakeupsPage({ studentId }: StudentMakeupsPageProps) {
  const { data: student, isLoading: isLoadingStudent } =
    useGetStudentById(studentId);
  const { data: sessionsData } = useGetAllSessions({
    page: 1,
    limit: 100,
    includeArchivedSessions: true,
  });
  const { data: makeupSettings } = useGetMakeupSettings();

  const [selectedSession, setSelectedSession] = useState<
    SessionOption | undefined | null
  >(ALL_SESSIONS_OPTION);
  const [manageBooking, setManageBooking] = useState<MakeupBooking | null>(
    null,
  );

  const isAllSessions =
    !selectedSession?.value || selectedSession.value === ALL_SESSIONS_ID;
  const sessionId = isAllSessions ? '' : selectedSession.value;

  const sessionNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const session of sessionsData?.sessions ?? []) {
      map.set(session._id, session.name);
    }
    return map;
  }, [sessionsData?.sessions]);

  const enrolledSessions = useMemo(() => {
    const byId = new Map<string, EnrolledSession>();
    for (const enrollment of student?.enrollments ?? []) {
      const session = getEnrollmentSession(enrollment, sessionNameById);
      if (!session) continue;
      if (!byId.has(session.id)) {
        byId.set(session.id, session);
      }
    }
    return Array.from(byId.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [student?.enrollments, sessionNameById]);

  const balanceQueries = useQueries({
    queries: enrolledSessions.map((session) => ({
      queryKey: [KEYS.MAKEUP_BALANCES, studentId, session.id],
      queryFn: () => getMakeupBalancesAPI({ studentId, sessionId: session.id }),
      enabled: !!studentId && !!session.id,
    })),
  });

  const sessionBalanceRows = useMemo<Array<SessionBalanceRow>>(() => {
    return enrolledSessions.map((session, index) => {
      const query = balanceQueries[index];
      return {
        sessionId: session.id,
        sessionName: session.name,
        balances: query?.data,
        isLoading: query?.isLoading ?? false,
        isError: query?.isError ?? false,
      };
    });
  }, [balanceQueries, enrolledSessions]);

  const bookingSessionOptions = useMemo(() => {
    return [
      ALL_SESSIONS_OPTION,
      ...enrolledSessions.map((session) => ({
        label: session.name,
        value: session.id,
      })),
    ];
  }, [enrolledSessions]);

  const {
    data: bookings = [],
    isLoading: isLoadingBookings,
    isError: isBookingsError,
  } = useGetMakeupsByStudent({ studentId }, !!studentId);

  const filteredBookings = useMemo(() => {
    if (isAllSessions) return bookings;
    return bookings.filter(
      (booking) => getBookingSessionId(booking) === sessionId,
    );
  }, [bookings, isAllSessions, sessionId]);

  const balanceColumns = useMemo(() => {
    const helper = createColumnHelper<SessionBalanceRow>();
    return [
      helper.accessor('sessionName', {
        header: 'Session',
        cell: (info) => <span className='font-medium'>{info.getValue()}</span>,
      }),
      helper.display({
        id: 'family',
        header: 'Family credits',
        cell: ({ row }) => {
          const { balances, isLoading, isError } = row.original;
          if (isLoading) {
            return (
              <span className='inline-flex items-center gap-1.5 text-muted-foreground'>
                <Loader2 className='size-3.5 animate-spin' />
                Loading…
              </span>
            );
          }
          if (isError || !balances) {
            return <span className='text-destructive'>Could not load</span>;
          }
          const exhausted = balances.family.remaining <= 0;
          return (
            <span className={cn(exhausted && 'text-destructive')}>
              {formatCreditCell(
                balances.family.remaining,
                balances.family.limit,
              )}
            </span>
          );
        },
      }),
      helper.display({
        id: 'student',
        header: 'Student allotment',
        cell: ({ row }) => {
          const { balances, isLoading, isError } = row.original;
          if (isLoading) {
            return (
              <span className='inline-flex items-center gap-1.5 text-muted-foreground'>
                <Loader2 className='size-3.5 animate-spin' />
                Loading…
              </span>
            );
          }
          if (isError || !balances) {
            return <span className='text-destructive'>Could not load</span>;
          }
          const exhausted = balances.student.remaining <= 0;
          return (
            <span className={cn(exhausted && 'text-destructive')}>
              {formatCreditCell(
                balances.student.remaining,
                balances.student.limit,
              )}
            </span>
          );
        },
      }),
    ];
  }, []);

  const columns = useMemo(() => {
    const helper = createColumnHelper<MakeupBooking>();
    return [
      helper.accessor('targetDate', {
        header: 'Makeup date',
        cell: (info) => formatMakeupDate(info.getValue()),
      }),
      helper.accessor('targetEnrollmentId', {
        header: 'Makeup class',
        cell: (info) => getEnrollmentTitle(info.getValue()),
      }),
      helper.display({
        id: 'session',
        header: 'Session',
        cell: ({ row }) => {
          const id = getBookingSessionId(row.original);
          return sessionNameById.get(id) ?? '—';
        },
      }),
      helper.display({
        id: 'absence',
        header: 'Absence',
        cell: ({ row }) => {
          const title = getEnrollmentTitle(row.original.sourceEnrollmentId);
          const date = formatMakeupDate(row.original.sourceAttendanceDate);
          return (
            <span>
              {title}
              <span className='text-muted-foreground'> · {date}</span>
            </span>
          );
        },
      }),
      helper.accessor('status', {
        header: 'Status',
        cell: (info) => <MakeupStatusBadge status={info.getValue()} />,
      }),
      helper.accessor('createdAt', {
        header: 'Booked',
        cell: (info) => formatBookedAt(info.getValue()),
      }),
      helper.display({
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const booking = row.original;
          const canManage =
            booking.status === 'scheduled' || booking.status === 'requested';
          if (!canManage) {
            return <span className='text-xs text-muted-foreground'>—</span>;
          }
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  className='size-8'
                  aria-label='Manage makeup'
                >
                  <MoreHorizontal className='size-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuItem
                  onClick={() => {
                    setManageBooking(booking);
                  }}
                >
                  Reschedule / Cancel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      }),
    ];
  }, [sessionNameById]);

  const manageSessionId =
    [
      manageBooking ? getBookingSessionId(manageBooking) : '',
      sessionId,
      enrolledSessions[0]?.id,
    ].find((value) => Boolean(value)) ?? '';

  if (isLoadingStudent) {
    return (
      <div className='flex items-center gap-2 text-sm text-muted-foreground'>
        <Loader2 className='size-4 animate-spin' />
        Loading student…
      </div>
    );
  }

  if (!student) {
    return <p className='text-sm text-muted-foreground'>Student not found.</p>;
  }

  return (
    <div className='space-y-8 pb-40'>
      <div>
        <h2 className='text-xl font-semibold'>Makeup history</h2>
        <p className='mt-1 text-sm text-muted-foreground'>
          Remaining balances by session for {student.name}.
        </p>
      </div>

      {enrolledSessions.length === 0 ? (
        <p className='rounded-lg border border-dashed p-4 text-sm text-muted-foreground'>
          No active session enrollments. Balances appear once this student is
          enrolled in a class.
        </p>
      ) : (
        <EnrollioTable
          ariaLabel='Student makeup balances'
          totalLabel='Session Balances'
          filterHeader={(table) => (
            <EnrollioTableSearch
              placeholder='Search sessions...'
              value={
                (table.getColumn('sessionName')?.getFilterValue() as
                  | string
                  | undefined) ?? ''
              }
              onChange={(value) => {
                table.getColumn('sessionName')?.setFilterValue(value);
              }}
            />
          )}
          toolbar={(table) => <EnrollioTableColumnToggle table={table} />}
          columns={balanceColumns}
          data={sessionBalanceRows}
        />
      )}

      <div className='space-y-4'>
        <div>
          <div>
            <h2 className='text-xl font-semibold'>Bookings</h2>
            <p className='mt-1 text-sm text-muted-foreground'>
              Makeup bookings for {student.name}.
            </p>
          </div>
        </div>

        {isLoadingBookings ? (
          <div className='flex items-center gap-2 text-sm text-muted-foreground'>
            <Loader2 className='size-4 animate-spin' />
            Loading makeup history…
          </div>
        ) : isBookingsError ? (
          <p className='text-sm text-destructive'>
            Could not load makeup history. Please try again.
          </p>
        ) : (
          <EnrollioTable
            ariaLabel='Student makeup bookings'
            totalLabel='Bookings'
            filterHeader={
              <div className='w-full sm:w-72'>
                <p className='mb-1.5 text-sm font-medium'>Session</p>
                <Select
                  options={bookingSessionOptions}
                  value={selectedSession}
                  onChange={(value) => {
                    setSelectedSession(
                      (value as SessionOption | null) ?? ALL_SESSIONS_OPTION,
                    );
                  }}
                  isClearable={false}
                  placeholder='Filter by session'
                  styles={sessionFilterStyles}
                  menuPosition='fixed'
                  menuPortalTarget={
                    typeof document !== 'undefined' ? document.body : null
                  }
                />
              </div>
            }
            toolbar={(table) => <EnrollioTableColumnToggle table={table} />}
            columns={columns}
            data={filteredBookings}
            emptyContent={
              isAllSessions
                ? 'No makeup bookings yet.'
                : 'No makeup bookings for this session yet.'
            }
          />
        )}
      </div>

      <ManageMakeupSheet
        open={!!manageBooking}
        onOpenChange={(open) => {
          if (!open) setManageBooking(null);
        }}
        booking={manageBooking}
        student={student}
        sessionId={manageSessionId}
        sendConfirmationDefault={
          makeupSettings?.makeupConfirmationEmailEnabled ?? true
        }
      />
    </div>
  );
}
